import React from "react";
import styled from "styled-components";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import TooltipSimple from "../../../common/tooltip/TooltipSimple";

// #region SCOPED CSS
const StyledCard = styled.div`
    &.overview-card {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 4px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        min-width: 200px;
    }

    /* --- Card title --- */
    .card-title {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: var(--overline-font-size, 14px);
        letter-spacing: var(--overline-letter-spacing, 0.015em);
        font-weight: var(--overline-font-weight, 500);
        text-transform: uppercase;
    }

    /* --- Divider line --- */
    .card-divider {
        border: none;
        height: 1px;
        background: #3c485a;
        width: 100%;
        margin: 6px 0;
    }

    /* --- Body layout: left metrics + right change panel --- */
    .card-body {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: stretch;
    }

    .card-metrics {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        min-width: 0;
    }

    /* --- Shared metric row --- */
    .metric-row {
        display: flex;
        flex-direction: column;
        gap: 0px;
    }

    .metric-label {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    .metric-info-icon {
        flex-shrink: 0;
        width: 10px;
        height: 10px;
        overflow: visible;
        cursor: pointer;
    }

    /* --- Primary value (blue, plan/budget) --- */
    .metric-value--primary {
        display: flex;
        align-items: center;
        height: 36px;
    }

    .metric-value--primary .value-amount {
        color: var(--text-and-icons-blue-500, #00aeef);
        font-size: var(--heading-4-font-size, 18px);
        font-weight: var(--heading-4-font-weight, 400);
    }

    .metric-value--primary .value-unit {
        color: var(--text-and-icons-blue-500, #00aeef);
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    /* --- Secondary value (green, committed) --- */
    .metric-value--secondary {
        display: flex;
        align-items: center;
        height: 36px;
    }

    .metric-value--secondary .value-amount {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: var(--heading-2-font-size, 24px);
        letter-spacing: var(--heading-2-letter-spacing, -0.005em);
        font-weight: var(--heading-2-font-weight, 400);
    }

    .metric-value--secondary .value-unit {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: var(--subtitle-1-font-size, 14px);
        letter-spacing: var(--subtitle-1-letter-spacing, 0.0025em);
        font-weight: var(--subtitle-1-font-weight, 400);
    }

    /* --- Tertiary value (gray, comparable-season purchased/actual) --- */
    .metric-value--tertiary {
        display: flex;
        align-items: center;
        color: var(--text-and-icons-gray-200, #7c868b);
    }

    .metric-value--tertiary .value-amount {
        font-size: var(--heading-4-font-size, 18px);
        font-weight: var(--heading-4-font-weight, 400);
    }

    .metric-value--tertiary .value-unit {
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    /* --- Change panel (right side, separated by left border) --- */
    .change-panel {
        border-left: 1px solid var(--backgrounds-and-lines-n-200, #262e3e);
        padding-left: 16px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        justify-content: center;
        flex: 1;
        min-width: 0;
    }

    .change-value {
        display: flex;
        align-items: center;
        color: var(--text-and-icons-green-500, #00bb00);
    }

    .change-value .value-amount {
        font-size: var(--heading-3-font-size, 20px);
        font-weight: var(--heading-3-font-weight, 400);
    }

    .change-value .value-unit {
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    .change-percentage {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: var(--subtitle-1-font-size, 14px);
        letter-spacing: var(--subtitle-1-letter-spacing, 0.0025em);
        font-weight: var(--subtitle-1-font-weight, 400);
    }

    /* --- Status footer --- */
    .card-status {
        font-size: var(--subtitle-2-font-size, 12px);
        font-weight: var(--subtitle-2-font-weight, 400);
    }

    .status-label {
        color: var(--text-and-icons-gray-100, #d1d1d1);
    }

    .status-value {
        color: var(--text-and-icons-green-500, #00bb00);
    }
`;
// #endregion

// #region CONFIG

/**
 * Card type configurations defining the labels and tooltips for each of the 4 overview cards.
 * Data values are passed via props from the parent page.
 */
export const OVERVIEW_CARD_TYPES = Object.freeze({
    BUYING_VALUE: {
        key: "buyingValue",
        title: "BUYING VALUE",
        primaryLabel: "Planned budget",
        secondaryLabel: "Committed Order",
        tertiaryLabel: "{comparableSeason} Purchased",
        changeLabel: "Change vs {comparableSeason}",
        statusPrefix: "Status",
        tooltips: {
            primary: {
                header: "Planned Budget",
                title: "Approved buying budget for this Buying Cycle."
            },
            secondary: {
                header: "Committed Order",
                title: "Total purchase value of the confirmed order within the selected scope."
            },
            tertiary: {
                header: "{comparableSeason} Purchased",
                title: "Comparable-season purchase value, converted using this Buying Cycle's exchange rate."
            },
            change: {
                header: "Change vs {comparableSeason}",
                title: "Difference between the Committed Order value and the comparable-season purchased value."
            }
        }
    },
    UNITS: {
        key: "units",
        title: "UNITS",
        primaryLabel: "Buy plan",
        secondaryLabel: "Committed Order",
        tertiaryLabel: "{comparableSeason} Purchased",
        changeLabel: "Change vs {comparableSeason}",
        statusPrefix: "Status",
        tooltips: {
            primary: {
                header: "Buy Plan",
                title: "Number of units recommended in the nanaBO Buy Plan."
            },
            secondary: {
                header: "Committed Order",
                title: "Number of units included in the confirmed order."
            },
            tertiary: {
                header: "{comparableSeason} Purchased",
                title: "Actual number of units purchased in the comparable season."
            },
            change: {
                header: "Change vs {comparableSeason}",
                title: "Difference between Committed Order units and comparable-season purchased units."
            }
        }
    },
    ESTIMATED_SELL_THROUGH: {
        key: "estimatedSellThrough",
        title: "ESTIMATED SELL-THROUGH",
        primaryLabel: "Buy plan",
        secondaryLabel: "Committed Order",
        tertiaryLabel: "{comparableSeason} Actual",
        changeLabel: "Change vs {comparableSeason}",
        statusPrefix: "Status",
        tooltips: {
            primary: {
                header: "Buy Plan",
                title: "Estimated sell-through if the recommended Buy Plan is followed."
            },
            secondary: {
                header: "Committed Order",
                title: "Estimated sell-through for the confirmed order."
            },
            tertiary: {
                header: "{comparableSeason} Actual",
                title: "Actual sell-through achieved in the comparable season."
            },
            change: {
                header: "Change vs {comparableSeason}",
                title: "Difference between the Committed Order estimate and comparable-season actual sell-through."
            }
        }
    },
    ESTIMATED_GROSS_PROFIT: {
        key: "estimatedGrossProfit",
        title: "ESTIMATED GROSS PROFIT",
        primaryLabel: "Buy plan",
        secondaryLabel: "Committed Order",
        tertiaryLabel: "{comparableSeason} Actual",
        changeLabel: "Change vs {comparableSeason}",
        statusPrefix: "Status",
        tooltips: {
            primary: {
                header: "Buy Plan",
                title: "Estimated gross profit if the recommended Buy Plan is followed."
            },
            secondary: {
                header: "Committed Order",
                title: "Estimated gross profit from the confirmed order."
            },
            tertiary: {
                header: "{comparableSeason} Actual",
                title: "Actual gross profit achieved in the comparable season."
            },
            change: {
                header: "Change vs {comparableSeason}",
                title: "Difference between the Committed Order estimate and comparable-season actual gross profit."
            }
        }
    },
    COST: {
        key: "cost",
        title: "COST",
        primaryLabel: "Planned avg. cost",
        secondaryLabel: null,
        tertiaryLabel: "{comparableSeason} avg. cost",
        changeLabel: "Change vs {comparableSeason}",
        statusPrefix: "Status",
        tooltips: {
            primary: {
                header: "Planned avg. cost",
                title: "Planned average cost per unit."
            },
            tertiary: {
                header: "{comparableSeason} avg. cost",
                title: "Average cost per unit in the comparable season."
            },
            change: {
                header: "Change vs {comparableSeason}",
                title: "Difference in average unit cost compared to the comparable season."
            }
        }
    }
});

// #endregion

/**
 * BIOverviewCard renders a single overview metrics card for the Buying Cycle Overview page.
 *
 * @param {Object} props
 * @param {Object} props.cardType - One of the OVERVIEW_CARD_TYPES entries defining the card labels.
 * @param {Object} props.data - The data values to display:
 * @param {Object} props.data.primary - { value: string, unit: string } — Plan/budget row (blue).
 * @param {Object} props.data.secondary - { value: string, unit: string } — Committed order row (green).
 * @param {Object} props.data.tertiary - { value: string, unit: string } — Comparable-season row (gray).
 * @param {Object} props.data.change - { value: string, unit: string, percentage: string } — Change panel (green).
 * @param {string} props.data.status - Status text to display in the footer.
 * @param {string} props.comparableSeasonName - Season name used in dynamic labels and tooltips.
 * @param {string} [props.primaryLabel] - Optional page-specific primary label.
 * @param {boolean} [props.showSecondary=true] - Whether to show the configured secondary row.
 */
export default function BIOverviewCard({
    cardType = OVERVIEW_CARD_TYPES.BUYING_VALUE,
    data = {},
    comparableSeasonName = "Comparable season",
    primaryLabel,
    showSecondary = true
}) {
    const { primary, secondary, tertiary, change, status } = data;
    const tooltips = cardType?.tooltips;
    const resolveCopy = value => {
        if (typeof value !== "string") return value;
        return value.replaceAll("{comparableSeason}", comparableSeasonName);
    };

    const renderInfoIconWithTooltip = tooltipConfig => {
        const icon = <InfoCircle className="metric-info-icon" />;

        if (!tooltipConfig) return icon;

        return (
            <TooltipSimple
                header={resolveCopy(tooltipConfig.header)}
                title={resolveCopy(tooltipConfig.title)}
                placement="top"
                centered={true}>
                {icon}
            </TooltipSimple>
        );
    };

    return (
        <StyledCard className="overview-card">
            {/* Card title */}
            <div className="card-title">{cardType.title}</div>

            <hr className="card-divider" />

            {/* Body: left metrics + right change panel */}
            <div className="card-body">
                {/* Left: primary, secondary, tertiary metrics stacked */}
                <div className="card-metrics">
                    {/* Primary metric (Plan/Budget — blue) */}
                        <div className="metric-row">
                            <div className="metric-label">
                                <span>{primaryLabel || cardType.primaryLabel}</span>
                                {renderInfoIconWithTooltip(tooltips?.primary)}
                        </div>
                        <div className="metric-value--primary">
                            <span className="value-amount">{primary?.value ?? "--"}</span>
                            {primary?.unit && (
                                <>
                                    &nbsp;<span className="value-unit">{primary.unit}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Secondary metric (Committed Order — green) */}
                    {showSecondary && cardType.secondaryLabel && (
                        <div className="metric-row">
                            <div className="metric-label">
                                <span>{cardType.secondaryLabel}</span>
                                {renderInfoIconWithTooltip(tooltips?.secondary)}
                            </div>
                            <div className="metric-value--secondary">
                                <span className="value-amount">{secondary?.value ?? "--"}</span>
                                {secondary?.unit && (
                                    <>
                                        &nbsp;<span className="value-unit">{secondary.unit}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tertiary metric (comparable season — gray) */}
                    <div className="metric-row">
                        <div className="metric-label">
                            <span>{resolveCopy(cardType.tertiaryLabel)}</span>
                            {renderInfoIconWithTooltip(tooltips?.tertiary)}
                        </div>
                        <div className="metric-value--tertiary">
                            <span className="value-amount">{tertiary?.value ?? "--"}</span>
                            {tertiary?.unit && (
                                <>
                                    &nbsp;<span className="value-unit">{tertiary.unit}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Change panel */}
                <div className="change-panel">
                    <div className="metric-label">
                        <span>{resolveCopy(cardType.changeLabel)}</span>
                        {renderInfoIconWithTooltip(tooltips?.change)}
                    </div>
                    <div className="change-value">
                        <span className="value-amount">{change?.value ?? "--"}</span>
                        {change?.unit && (
                            <>
                                &nbsp;<span className="value-unit">{change.unit}</span>
                            </>
                        )}
                    </div>
                    {change?.percentage && <div className="change-percentage">{change.percentage}</div>}
                </div>
            </div>

            <hr className="card-divider" />

            {/* Status footer */}
            <div className="card-status">
                <span className="status-label">{cardType.statusPrefix}: </span>
                <span className="status-value">{status ?? "--"}</span>
            </div>
        </StyledCard>
    );
}
